import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { Handshake, Plus, Trash2, Pencil, Building } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ImageUpload } from "../../components/ImageUpload";
import { mediaUrl } from "../../media";

const EMPTY = { name:"", category:"", benefit:"", code:"", image_url:"", description:"", business_partner_id:"", instagram_url:"" };

const CompanySelect = ({ value, onChange, companies, testid }) => (
  <select value={value || ""} onChange={e=>onChange(e.target.value)} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid={testid}>
    <option value="">Empresa vinculada (usar foto da empresa)</option>
    {companies.map(c => <option key={c.business_partner_id} value={c.business_partner_id}>{c.name}{c.image_url ? "" : " (sem foto)"}</option>)}
  </select>
);

export default function PartnersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [companies, setCompanies] = useState([]);

  const load = () => { api.get("/partnerships").then(r=>setItems(r.data)).catch(()=>{}); };
  useEffect(() => { load(); api.get("/business-partners/public").then(r=>setCompanies(r.data)).catch(()=>{}); }, []);

  const create = async () => {
    if (!form.name || !form.code) return toast.error("Nome e cupom obrigatórios");
    try {
      await api.post("/admin/partnerships", { ...form, code: form.code.toUpperCase() });
      toast.success("Parceria criada");
      setForm(EMPTY); load();
    } catch { toast.error("Falha ao criar"); }
  };

  const save = async () => {
    if (!editing) return;
    try {
      const { partnership_id, ...body } = editing;
      await api.patch(`/admin/partnerships/${partnership_id}`, { ...body, code: (body.code||"").toUpperCase() });
      toast.success("Parceria atualizada");
      setEditing(null); load();
    } catch { toast.error("Falha ao salvar"); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir parceria?")) return;
    await api.delete(`/admin/partnerships/${id}`);
    toast.success("Excluída"); load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {items.map(p => (
          <div key={p.partnership_id} className="card-arya p-4 flex flex-wrap items-start gap-4" data-testid={`admin-pt-${p.partnership_id}`}>
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[var(--cream-100)] flex-shrink-0 grid place-items-center border border-[var(--line)]">
              {p.image_url ? (
                <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" onError={(e)=>{e.target.style.display='none';}}/>
              ) : (
                <Building size={24} className="text-[color:var(--sun-500)]"/>
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              <span className="tag-sun">{p.category || "Parceria"}</span>
              <p className="font-serif text-xl mt-1">{p.name}</p>
              <p className="text-sm text-[color:var(--ink-500)]">{p.benefit}</p>
              {p.description && <p className="text-xs text-[color:var(--ink-500)] mt-1">{p.description}</p>}
              <p className="mt-2 inline-block font-mono text-xs bg-[rgba(233,204,133,.15)] text-[color:var(--sun-700)] px-2 py-1 rounded-full">Cupom: {p.code}</p>
            </div>
            <div className="flex gap-1 items-start">
              <button onClick={()=>setEditing(/instagram\.com/i.test(p.image_url||"") && !p.instagram_url ? {...p, instagram_url:p.image_url} : {...p})} className="btn-ghost !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`edit-pt-${p.partnership_id}`}><Pencil size={12}/> Editar</button>
              <button onClick={()=>del(p.partnership_id)} className="p-2 text-[color:var(--danger)]" data-testid={`del-pt-${p.partnership_id}`}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
        {items.length===0 && <div className="card-arya p-10 text-center text-[color:var(--ink-500)]">Nenhuma parceria cadastrada.</div>}
      </div>

      <div className="card-arya p-6 h-fit">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Handshake size={16}/> Nova parceria</h3>
        <div className="space-y-2">
          <input placeholder="Nome da parceria" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="input-new-pt-name"/>
          <input placeholder="Categoria (ex: Estética, Moda)" value={form.category} onChange={e=>setForm({...form, category:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <input placeholder="Benefício (ex: 15% OFF)" value={form.benefit} onChange={e=>setForm({...form, benefit:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <input placeholder="Cupom (ex: ARYA15)" value={form.code} onChange={e=>setForm({...form, code:e.target.value.toUpperCase()})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm font-mono"/>
          <ImageUpload value={form.image_url} onChange={(u)=>setForm({...form, image_url:u})} label="Foto da parceria (aparece no site)" testid="new-pt-image"/>
          <CompanySelect value={form.business_partner_id} onChange={(v)=>setForm({...form, business_partner_id:v})} companies={companies} testid="new-pt-company"/>
          <input placeholder="Link do Instagram (opcional)" value={form.instagram_url} onChange={e=>setForm({...form, instagram_url:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="new-pt-instagram"/>
          <textarea rows="2" placeholder="Descrição (opcional)" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}
                    className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <button onClick={create} className="btn-gold w-full inline-flex items-center justify-center gap-1" data-testid="btn-create-pt"><Plus size={14}/> Adicionar parceria</button>
          <p className="text-[10px] text-[color:var(--ink-500)] leading-relaxed pt-1">
            Envie uma foto da parceria. Ela aparece junto com as informações e o cupom na página <b>/parcerias</b>.
          </p>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o)=> !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Editar parceria</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-2">
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">NOME</label>
              <input value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="edit-pt-name"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">CATEGORIA</label>
              <input value={editing.category||""} onChange={e=>setEditing({...editing, category:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">BENEFÍCIO</label>
              <input value={editing.benefit||""} onChange={e=>setEditing({...editing, benefit:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">CUPOM</label>
              <input value={editing.code||""} onChange={e=>setEditing({...editing, code:e.target.value.toUpperCase()})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm font-mono"/>
              <ImageUpload value={editing.image_url && !/instagram\.com/i.test(editing.image_url) ? editing.image_url : ""} onChange={(u)=>setEditing({...editing, image_url:u})} label="Foto da parceria" testid="edit-pt-image"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">EMPRESA VINCULADA</label>
              <CompanySelect value={editing.business_partner_id} onChange={(v)=>setEditing({...editing, business_partner_id:v})} companies={companies} testid="edit-pt-company"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">INSTAGRAM</label>
              <input placeholder="Link do Instagram (opcional)" value={editing.instagram_url||""} onChange={e=>setEditing({...editing, instagram_url:e.target.value})}
                     className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="edit-pt-instagram"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">DESCRIÇÃO</label>
              <textarea rows="2" value={editing.description||""} onChange={e=>setEditing({...editing, description:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
            </div>
          )}
          <DialogFooter>
            <button onClick={()=>setEditing(null)} className="btn-ghost">Cancelar</button>
            <button onClick={save} className="btn-gold" data-testid="btn-save-edit-pt">Salvar alterações</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
